import { friendlyMetricName } from '@upright/common/model/input/pai-assessments';
import { PaiMetric } from '@upright/common/model/input/pai-assessments/types';
import { MetricNature } from '@upright/common/model/input/types';
import { KBUrl, KBLink } from '@upright/ui/common/upright/kb-urls';
import { useDefaultCompanyResult } from '@upright/ui/views/model-entity-views/company-view/context/use-default-company-result';
import { format } from 'd3-format';
import { max, toPairs, values } from 'lodash';
import styled from 'styled-components';
import { useHasSubpath } from '@upright/ui/common/generic/hooks/use-has-subpath';

export function GhgText(props: { companyId: string | undefined }) {
  return (
    <>
      <sup>1</sup>Upright assigns all global private sector emissions to companies exactly once, to avoid double
      counting. This is different from the conventional Scope 1/2/3 GHG emissions metrics.
      <PossibleGhgDisclosureText companyId={props.companyId} />
      <div style={{ marginTop: '1.0em' }}>
        <KBLink href={KBUrl.ATTRIBUTION_ILLUSTRATIVE_GHG_EXAMPLE} target="_blank">
          Read more in the Upright Knowledge Base
        </KBLink>
      </div>
    </>
  );
}

function PossibleGhgDisclosureText(props: { companyId: string | undefined }) {
  const inCompanyView = useHasSubpath('company');
  if (inCompanyView && props.companyId) {
    return <GhgDisclosureText companyId={props.companyId} />;
  }
  return <></>;
}

function GhgDisclosureText(props: { companyId: string }) {
  const modelResult = useDefaultCompanyResult(props.companyId);

  if (!modelResult) {
    return <></>;
  }

  const pais = modelResult.euPaiMetrics;
  if (!pais) {
    return <></>;
  }

  const ghgMetrics = [PaiMetric.GHG1, PaiMetric.GHG2, PaiMetric.GHG3];
  const ghgDisclosures = values(pais).filter(
    d => d.nature === MetricNature.DIRECT_DISCLOSURE && ghgMetrics.includes(d.metric),
  );

  if (ghgDisclosures.length === 0) {
    return <></>;
  }
  const maxYear = max(ghgDisclosures.map(d => d.year));
  const latestYearDisclosures = ghgDisclosures.filter(p => p.year === maxYear);
  const latestYearDisclosureCount = latestYearDisclosures.length;

  const formatter = format(',r');

  if (latestYearDisclosureCount === 0) {
    return <></>;
  } else if (latestYearDisclosureCount === 1) {
    const [key, value] = toPairs(latestYearDisclosures)[0];
    return (
      <Root>
        In {maxYear}, the company reported {friendlyMetricName(key as PaiMetric)} of <b>{formatter(value.value)}</b>{' '}
        tonnes.
      </Root>
    );
  } else {
    return (
      <>
        <Root>
          In <b>{maxYear}</b>, the company reported:
          <ul>
            {ghgMetrics.map(m => {
              const disclosure = latestYearDisclosures.find(d => d.metric === m);
              if (!disclosure) {
                return <></>;
              }
              return (
                <li key={m}>
                  {friendlyMetricName(m)} of <b>{formatter(disclosure.value)}</b> tonnes.
                </li>
              );
            })}
          </ul>
        </Root>
      </>
    );
  }
}

const Root = styled.div`
  margin-top: 1em;

  ul {
    margin-top: 0.3em;

    li {
    }
  }
`;
